import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { constants } from '../../../constants';
import { DataFilters, DataFiltersService } from './data-filters.service';
import { map, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import {
  AgeGenderComposition,
  ConsumerData,
  Continents,
  SizeOfPrize,
  SizeOfPrizeGeneralInfo,
  SizeOfPrizeRanking,
  SizeOfPrizeTop,
  TotalDistribution,
} from '../models/statistics.model';
import { SpendingGroup } from '../models/spending-group.model';
import { IncomeGroup } from '../models/income-group.model';
import { Region } from '../models/region-model';
import { AuthService } from '../../auth/services/auth.service';

const GroupNameMaping = {
  'Vulnerable and Poor': '<13',
  'Lower Middle': '13-45',
  'Core Middle': '45-90',
  'Upper Middle': '90-130',
  Rich: '>130',
};

@Injectable()
export class StatisticsService implements OnDestroy {
  public dataFilters: DataFilters = new DataFilters();
  private ngUnsubscribe: Subject<void> = new Subject();
  public getConsumerData$ = new Subject<{
    startYear: number;
    endYear: number;
  }>();
  public getAgeGenderComposition$ = new ReplaySubject<void>(1);
  public getTotalDistribution$ = new Subject<void>();
  public getContinents$ = new Subject<void>();
  public getSizeOfPrize$ = new Subject<void>();
  public getSizeOfPrizeTop$ = new Subject<void>();
  public getSizeOfPrizeGeneralInfo$ = new Subject<void>();
  public getSizeOfPrizeRanking$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private dataFiltersService: DataFiltersService
  ) {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        this.dataFilters = filters;
      });
  }

  public buildRequest() {
    const request: any = {};
    if (
      this.dataFilters.ageGroup.lessThan ||
      this.dataFilters.ageGroup.greaterThan
    ) {
      request.age = {
        lte: this.dataFilters.ageGroup.lessThan,
        gte: this.dataFilters.ageGroup.greaterThan,
      };
    }
    if (this.dataFilters.spendingGroup) {
      if (this.dataFilters.spendingGroup instanceof SpendingGroup) {
        request.spending = {
          lte: this.dataFilters.spendingGroup.lessThan,
          gte: this.dataFilters.spendingGroup.greaterThan,
        };
      } else if (this.dataFilters.spendingGroup instanceof IncomeGroup) {
        request.income = {
          lte: this.dataFilters.spendingGroup.lessThan,
          gte: this.dataFilters.spendingGroup.greaterThan,
        };
      }
    }
    if (this.dataFilters.regionSelection.cities.size > 0) {
      const city = this.dataFilters.regionSelection.cities
        .values()
        .next().value;
      if (city) {
        request.cityId = city.name;
      }
      if (!request.countryCodes) {
        request.countryCodes = Array.from(
          this.dataFilters.regionSelection.countries.keys()
        );
      }
    } else if (this.dataFilters.regionSelection.countries.size > 0) {
      request.countryCodes = Array.from(
        this.dataFilters.regionSelection.countries.keys()
      );
    } else if (this.dataFilters.regionSelection.regions.size > 0) {
      request.continentCode = (
        this.dataFilters.regionSelection.regions.values().next().value as Region
      ).code;
    }
    if (this.dataFilters.freemiumCategory) {
      request.coicopCategoryCode = this.dataFilters.freemiumCategory.code;
    }

    request.year = this.dataFilters.year;
    request.gender = this.dataFilters.gender?.toUpperCase();
    request.spendingPeriod = this.dataFilters.spendingPer ? 'YEAR' : 'DAY';
    request.currencyType = this.dataFilters.currency ? 'NOMINAL_USD' : 'PPP';

    return request;
  }

  public getConsumerData({
    endYear,
    startYear,
    countryCode,
    cityName,
  }: {
    endYear: number;
    startYear: number;
    cityName?: string;
    countryCode?: string;
  }) {
    const request = this.buildRequest();
    if (cityName) {
      (request.cityId = cityName), (request.countryCodes = [countryCode]);
    }
    return this.http.post<ConsumerData>(
      constants.uriParts.statisticsConsumerData,
      {
        ...request,
        annualMarketYears: {
          lte: endYear,
          gte: startYear,
        },
      }
    );
  }

  public getAgeGenderComposition() {
    const request = this.buildRequest();
    return this.http.post<AgeGenderComposition>(
      constants.uriParts.statisticsAgeGenderComposition,
      {
        ...request,
      }
    );
  }

  public getTotalDistribution() {
    const request = this.buildRequest();
    return this.http
      .post<TotalDistribution>(constants.uriParts.statisticsTotalDistribution, {
        ...request,
      })
      .pipe(
        map((res) => {
          return this.mapGroupName(res);
        })
      );
  }

  public mapGroupName(res: TotalDistribution): TotalDistribution {
    const renamedSpendingData = res.spendingDatas.map((data) => ({
      ...data,
      groupName: GroupNameMaping[data.groupName],
    }));
    const renamedIncomeData = res.incomeDatas?.map((data) => ({
      ...data,
      groupName: GroupNameMaping[data.groupName],
    }));

    return {
      incomeDatas: renamedIncomeData,
      spendingDatas: renamedSpendingData,
    };
  }

  public getContinentsData() {
    const request = this.buildRequest();
    request.continentCode = undefined;
    return this.http.post<Continents>(constants.uriParts.statisticsContinents, {
      ...request,
    });
  }

  public getSizeOfPrize() {
    const request = this.buildRequest();
    return this.http.post<SizeOfPrize[]>(constants.uriParts.sizeOfPrize, {
      ...request,
    });
  }

  public getSizeOfPrizeGeneralInfo() {
    const request = this.buildRequest();
    return this.http.post<SizeOfPrizeGeneralInfo>(
      constants.uriParts.sizeOfPrizeGeneralInfo,
      {
        ...request,
      }
    );
  }

  public getSizeOfPrizeTop() {
    const request = this.buildRequest();
    return this.http.post<SizeOfPrizeTop[]>(constants.uriParts.sizeOfPrizeTop, {
      ...request,
    });
  }

  public getSizeOfPrizeRanking() {
    const request = this.buildRequest();
    return this.http.post<SizeOfPrizeRanking[]>(
      constants.uriParts.sizeOfPrizeRanking,
      {
        ...request,
      }
    );
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
